import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Pencil, Check, ArrowLeftRight, Search } from 'lucide-react'
import { customerApi } from '@/api/customers'
import { Tenant, Customer, CustomerCreateRequest, UserRole, CustomerCategory, CustomerPriceRule, CustomerPriceRuleCreateRequest } from '@/types'
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"

interface CustomerDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CustomerCreateRequest & { tenant_id?: string }) => Promise<void>
  tenants: Tenant[]
  customer?: Customer | null
  elevated?: boolean
}

type Tab = 'info' | 'price'

const EMPTY_RULE: CustomerPriceRuleCreateRequest = {
  from_area: '',
  to_area: '',
  price: 0,
  vehicle_type: '',
  is_bidirectional: true,
  note: '',
}

export default function CustomerDrawer({ isOpen, onClose, onSubmit, tenants, customer, elevated = false }: CustomerDrawerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('info')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [userRole, setUserRole] = useState<UserRole | null>(null)
  const [userTenantId, setUserTenantId] = useState<string | null>(null)
  const [formData, setFormData] = useState<CustomerCreateRequest>({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    note: '',
    category: CustomerCategory.NORMAL,
  })

  // Price rules state
  const [priceRules, setPriceRules] = useState<CustomerPriceRule[]>([])
  const [priceLoading, setPriceLoading] = useState(false)
  const [priceSearch, setPriceSearch] = useState('')
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)
  const [editingRule, setEditingRule] = useState<CustomerPriceRuleCreateRequest>(EMPTY_RULE)
  const [addingNew, setAddingNew] = useState(false)
  const [newRule, setNewRule] = useState<CustomerPriceRuleCreateRequest>(EMPTY_RULE)
  const [ruleSaving, setRuleSaving] = useState(false)

  useEffect(() => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const userData = JSON.parse(userStr)
        setUserRole(userData.role)
        setUserTenantId(userData.tenant_id)
      } catch {}
    }
  }, [])

  useEffect(() => {
    if (!selectedTenantId) {
      if (tenants.length > 0) {
        setSelectedTenantId(userRole === UserRole.TENANT_ADMIN && userTenantId ? userTenantId : tenants[0].id)
      } else if (userTenantId) {
        setSelectedTenantId(userTenantId)
      }
    }
  }, [tenants, userRole, userTenantId])

  useEffect(() => {
    if (isOpen) {
      setActiveTab('info')
      setAddingNew(false)
      setEditingRuleId(null)
      if (customer) {
        setFormData({
          name: customer.name,
          contact_person: customer.contact_person || '',
          phone: customer.phone,
          email: customer.email || '',
          address: customer.address || '',
          note: customer.note || '',
          tax_id: customer.tax_id || '',
          category: customer.category || CustomerCategory.NORMAL,
          booking_pin: customer.booking_pin || '',
        })
        setSelectedTenantId(customer.tenant_id)
      } else {
        setFormData({
          name: '',
          contact_person: '',
          phone: '',
          email: '',
          address: '',
          note: '',
          category: CustomerCategory.NORMAL,
          booking_pin: '',
        })
        setPriceRules([])
        if (tenants.length > 0) {
          setSelectedTenantId(userRole === UserRole.TENANT_ADMIN && userTenantId ? userTenantId : tenants[0].id)
        }
      }
    }
  // 用 tenants.length 而非 tenants 本身當依賴：呼叫端若每次都傳一個新的陣列字面值（即使內容相同），
  // 用陣列參照當依賴會導致這個 effect 在畫面重新渲染時被誤觸發，把使用者正在輸入的表單資料清空
  }, [isOpen, customer, tenants.length])

  // Load price rules when switching to price tab (edit mode only)
  useEffect(() => {
    if (isOpen && activeTab === 'price' && customer?.id) {
      setPriceLoading(true)
      customerApi.getPriceRules(customer.id)
        .then(setPriceRules)
        .catch(() => {})
        .finally(() => setPriceLoading(false))
    }
  }, [isOpen, activeTab, customer?.id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const raw = { ...formData, tenant_id: selectedTenantId || undefined }
      const payload = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== '')
      ) as typeof raw
      await onSubmit(payload)
      onClose()
    } catch {}
    finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveNewRule = async () => {
    if (!customer?.id || !newRule.from_area || !newRule.to_area || newRule.price < 0) return
    setRuleSaving(true)
    try {
      const created = await customerApi.createPriceRule(customer.id, {
        ...newRule,
        vehicle_type: newRule.vehicle_type || undefined,
        note: newRule.note || undefined,
      })
      setPriceRules(prev => [...prev, created].sort((a, b) => a.from_area.localeCompare(b.from_area)))
      setAddingNew(false)
      setNewRule(EMPTY_RULE)
    } catch {}
    finally {
      setRuleSaving(false)
    }
  }

  const handleSaveEditRule = async () => {
    if (!customer?.id || !editingRuleId) return
    setRuleSaving(true)
    try {
      const updated = await customerApi.updatePriceRule(customer.id, editingRuleId, {
        ...editingRule,
        vehicle_type: editingRule.vehicle_type || undefined,
        note: editingRule.note || undefined,
      })
      setPriceRules(prev => prev.map(r => r.id === editingRuleId ? updated : r))
      setEditingRuleId(null)
    } catch {}
    finally {
      setRuleSaving(false)
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    if (!customer?.id) return
    try {
      await customerApi.deletePriceRule(customer.id, ruleId)
      setPriceRules(prev => prev.filter(r => r.id !== ruleId))
    } catch {}
  }

  const startEdit = (rule: CustomerPriceRule) => {
    setEditingRuleId(rule.id)
    setEditingRule({
      from_area: rule.from_area,
      to_area: rule.to_area,
      price: rule.price,
      vehicle_type: rule.vehicle_type || '',
      is_bidirectional: rule.is_bidirectional,
      note: rule.note || '',
    })
  }

  const filteredRules = (() => {
    const q = priceSearch.trim().toLowerCase()
    if (!q) return priceRules
    return priceRules.filter(r =>
      r.from_area.toLowerCase().includes(q) ||
      r.to_area.toLowerCase().includes(q) ||
      (r.vehicle_type || '').toLowerCase().includes(q) ||
      (r.note || '').toLowerCase().includes(q)
    )
  })()

  const vehicleOptions = (tenants.find(t => t.id === selectedTenantId)?.vehicle_categories || [])
    .map(v => v.name)
    .filter(Boolean)

  if (!isOpen) return null

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 transition-opacity ${elevated ? 'z-[55]' : 'z-40'}`}
        onClick={onClose}
      />

      <div className={`fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-[500px] bg-white dark:bg-gray-800 shadow-xl overflow-hidden flex flex-col ${elevated ? 'z-[60]' : 'z-50'}`}>
        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white px-1">
            {customer ? '編輯客戶' : '新增客戶'}
          </h2>
          <div className="flex items-center gap-1.5">
            {activeTab === 'info' && (
              <>
                <Button type="button" variant="ghost" onClick={onClose} className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700">
                  取消
                </Button>
                <Button type="submit" form="customer-form" disabled={isSubmitting} className="h-8 px-4 text-xs font-bold shadow-sm">
                  {isSubmitting ? '儲存中...' : customer ? '更新客戶' : '建立客戶'}
                </Button>
              </>
            )}
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs — only show for existing customers */}
        {customer && (
          <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {(['info', 'price'] as Tab[]).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {tab === 'info' ? '基本資料' : '合約定價'}
              </button>
            ))}
          </div>
        )}

        {/* Info Tab */}
        {activeTab === 'info' && (
          <form id="customer-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
            {tenants.length > 0 && (
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">所屬商戶 *</Label>
                {userRole === UserRole.TENANT_ADMIN ? (
                  <div className="flex h-9 w-full items-center rounded-md border border-input bg-gray-50 px-3 py-1 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                    {tenants.find(t => t.id === selectedTenantId)?.name} ({tenants.find(t => t.id === selectedTenantId)?.code})
                  </div>
                ) : (
                  <select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    required
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="" disabled>請選擇商戶</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">類別</Label>
              <div className="flex gap-2">
                {([
                  { value: CustomerCategory.NORMAL, label: '一般客戶' },
                  { value: CustomerCategory.VIP, label: 'VIP' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: value })}
                    className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors cursor-pointer ${
                      formData.category === value
                        ? value === CustomerCategory.VIP
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'bg-blue-600 border-blue-600 text-white'
                        : 'border-input bg-transparent text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">公司名稱 *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">聯絡人</Label>
                <Input value={formData.contact_person || ''} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="選填" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">聯絡電話 *</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">Email</Label>
                <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="選填" />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">統一編號</Label>
                <Input value={formData.tax_id || ''} onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })} placeholder="選填" />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">地址</Label>
              <Input value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="選填" />
            </div>

            <div className="bg-blue-50/50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
              <Label className="mb-1 block text-[11px] font-bold text-blue-600 uppercase tracking-wider">訂單查詢 PIN</Label>
              <p className="text-xs text-gray-400 mb-2">未設定時客戶以手機末 4 碼驗證；可設為統編或自訂碼</p>
              <Input value={formData.booking_pin || ''} onChange={(e) => setFormData({ ...formData, booking_pin: e.target.value })} placeholder="留空 = 使用手機末 4 碼" maxLength={20} />
            </div>

            <div className="bg-gray-50/50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-800">
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">備註</Label>
              <Textarea value={formData.note || ''} onChange={(e) => setFormData({ ...formData, note: e.target.value })} placeholder="輸入客戶備註..." rows={4} className="resize-none" />
            </div>
          </form>
        )}

        {/* Price Rules Tab */}
        {activeTab === 'price' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-gray-400">設定客戶專屬合約定價，訂車時依出發地／目的地自動帶入金額</p>
              {!addingNew && (
                <Button
                  type="button"
                  size="sm"
                  className="h-7 px-3 text-xs gap-1 shrink-0"
                  onClick={() => { setAddingNew(true); setNewRule(EMPTY_RULE) }}
                >
                  <Plus className="w-3.5 h-3.5" />新增
                </Button>
              )}
            </div>

            {/* Search bar — show when there are rules */}
            {priceRules.length > 0 && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={priceSearch}
                  onChange={e => setPriceSearch(e.target.value)}
                  placeholder="搜尋地區、車型、備註..."
                  className="w-full h-8 pl-8 pr-7 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 outline-none focus:ring-1 focus:ring-primary"
                />
                {priceSearch && (
                  <button
                    type="button"
                    onClick={() => setPriceSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Add new rule form */}
            {addingNew && (
              <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50/50 dark:bg-blue-900/20 space-y-2">
                <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">新增定價規則</p>
                <PriceRuleForm rule={newRule} onChange={setNewRule} vehicleOptions={vehicleOptions} />
                <div className="flex gap-2 pt-1">
                  <Button type="button" size="sm" className="h-7 px-3 text-xs gap-1" onClick={handleSaveNewRule} disabled={ruleSaving || !newRule.from_area || !newRule.to_area}>
                    <Check className="w-3.5 h-3.5" />{ruleSaving ? '儲存中...' : '儲存'}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setAddingNew(false)}>取消</Button>
                </div>
              </div>
            )}

            {/* Rules list */}
            {priceLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">載入中...</p>
            ) : priceRules.length === 0 && !addingNew ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-sm">尚無合約定價規則</p>
                <p className="text-xs mt-1">點擊「新增」按鈕建立第一筆</p>
              </div>
            ) : filteredRules.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">找不到符合「{priceSearch}」的定價規則</p>
            ) : (
              filteredRules.map(rule => (
                <div key={rule.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {editingRuleId === rule.id ? (
                    <div className="p-3 space-y-2 bg-gray-50/50 dark:bg-gray-900/20">
                      <PriceRuleForm rule={editingRule} onChange={setEditingRule} vehicleOptions={vehicleOptions} />
                      <div className="flex gap-2 pt-1">
                        <Button type="button" size="sm" className="h-7 px-3 text-xs gap-1" onClick={handleSaveEditRule} disabled={ruleSaving}>
                          <Check className="w-3.5 h-3.5" />{ruleSaving ? '儲存中...' : '儲存'}
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setEditingRuleId(null)}>取消</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center px-3 py-2.5 gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-200">
                          <span className="truncate">{rule.from_area}</span>
                          {rule.is_bidirectional
                            ? <ArrowLeftRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            : <span className="text-gray-400 text-xs shrink-0">→</span>
                          }
                          <span className="truncate">{rule.to_area}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-bold text-blue-600">NT$ {rule.price.toLocaleString()}</span>
                          {rule.vehicle_type && <span className="text-xs text-gray-400">{rule.vehicle_type}</span>}
                          {rule.note && <span className="text-xs text-gray-400 truncate">{rule.note}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(rule)}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  )
}


interface PriceRuleFormProps {
  rule: CustomerPriceRuleCreateRequest
  onChange: (r: CustomerPriceRuleCreateRequest) => void
  vehicleOptions: string[]
}

function PriceRuleForm({ rule, onChange, vehicleOptions }: PriceRuleFormProps) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">出發地區 *</label>
          <Input
            value={rule.from_area}
            onChange={e => onChange({ ...rule, from_area: e.target.value })}
            placeholder="例：宜蘭"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">目的地區 *</label>
          <Input
            value={rule.to_area}
            onChange={e => onChange({ ...rule, to_area: e.target.value })}
            placeholder="例：新竹"
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">金額 (NT$) *</label>
          <Input
            type="number"
            min={0}
            value={rule.price}
            onChange={e => onChange({ ...rule, price: parseInt(e.target.value) || 0 })}
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">車型（選填）</label>
          <select
            value={rule.vehicle_type || ''}
            onChange={e => onChange({ ...rule, vehicle_type: e.target.value })}
            className="flex h-8 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer"
          >
            <option value="">不限車型</option>
            {vehicleOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={rule.is_bidirectional}
            onChange={e => onChange({ ...rule, is_bidirectional: e.target.checked })}
            className="w-4 h-4 rounded accent-blue-600"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">雙向（來回同價）</span>
        </label>
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-gray-500 mb-1">備註（選填）</label>
        <Input
          value={rule.note || ''}
          onChange={e => onChange({ ...rule, note: e.target.value })}
          placeholder="例：含稅、含停車費..."
          className="h-8 text-sm"
        />
      </div>
    </div>
  )
}
